import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import {
  IconArrowUp,
  IconBrain,
  IconInfoCircle,
  IconPaperclip,
  IconPlayerStopFilled,
  IconX,
} from '@tabler/icons-react';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { DriveFilePickerModal } from '@ayocore/platform/filemanager/client/components';
import type { UploadedFile } from '@ayocore/platform/filemanager/schemas/uploaded_file.schema';

import { AssistantChatController } from '../../controllers/AssistantChatController';
import { useAssistantChat } from '../../controllers/useAssistantChat';
import { useLlmStreamController } from '../../controllers/useLlmStreamController';
import type { ToolCallInfo, ToolCallStatus } from '../../controllers/LlmStreamController';
import { AssistantMessageCard } from './AssistantMessageCard';
import classes from './AssistantChat.module.css';

/** Цвет бейджа по статусу вызова инструмента */
const TOOL_STATUS_COLOR: Record<ToolCallStatus, string> = {
  pending: 'gray',
  executing: 'blue',
  completed: 'green',
  error: 'red',
  waiting_user_input: 'yellow',
};

/**
 * Чипы активных вызовов инструментов: пользователь видит, ЧТО делает ассистент.
 * Показываются только пока идёт стрим (после завершения ядро само чистит список).
 */
function ToolCallChips() {
  const toolCalls = useLlmStreamController(s => s.toolCalls) as Map<string, ToolCallInfo>;
  const isLoading = useLlmStreamController(s => s.isLoading) as boolean;
  if (!isLoading || toolCalls.size === 0) return null;
  return (
    <Group gap={4} className={classes.toolChips}>
      {Array.from(toolCalls.values()).map(call => (
        <Badge
          key={call.call_id}
          size='xs'
          variant='light'
          color={TOOL_STATUS_COLOR[call.status] ?? 'gray'}
        >
          {call.name}
        </Badge>
      ))}
    </Group>
  );
}

/**
 * Чат с ассистентом: история (с пагинацией «назад»), стрим, HITL-ответ, composer.
 * Самодостаточен — его можно монтировать в dock-панель, drawer или страницу.
 * Состояние живёт в контроллерах вне React и переживает размонтирование.
 */
export function AssistantChatView() {
  const controller = useMemo(() => AssistantChatController.getInstance(), []);
  const messages = useAssistantChat(s => s.messages);
  const isLoadingHistory = useAssistantChat(s => s.isLoadingHistory);
  const loadingOlder = useAssistantChat(s => s.loadingOlder);
  const hasMoreOlder = useAssistantChat(s => s.hasMoreOlder);
  const isStreaming = useAssistantChat(s => s.isStreaming);
  const error = useAssistantChat(s => s.error);
  const pendingUserInput = useLlmStreamController(s => s.pendingUserInput) as {
    call_id: string;
    prompt: string;
  } | null;
  const streamContent = useLlmStreamController(s => s.content) as string;
  const toolCalls = useLlmStreamController(s => s.toolCalls) as Map<string, ToolCallInfo>;

  // Статус текущей активности ассистента (от первого лица) по стрим-сигналам:
  // активный инструмент → «Работаю…»; текста ещё нет → «Думаю…» (идёт reasoning);
  // текст пошёл → «Печатаю…».
  const hasActiveTool = Array.from(toolCalls.values()).some(
    call => call.status === 'pending' || call.status === 'executing'
  );
  const statusLabel = hasActiveTool
    ? 'Работаю…'
    : streamContent.length === 0
      ? 'Думаю…'
      : 'Печатаю…';

  const [prompt, setPrompt] = useState('');
  /** Файл с Диска, приложенный к следующему сообщению (сервер положит его в /workspace) */
  const [attachedFile, setAttachedFile] = useState<UploadedFile | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const filePicker = useModal(DriveFilePickerModal);

  useEffect(() => {
    void controller.init();
  }, [controller]);

  // Автоскролл только при изменении ПОСЛЕДНЕГО сообщения (стрим/новое) —
  // подгрузка старых страниц сверху позицию не дёргает
  const last = messages[messages.length - 1];
  const lastId = last?.id;
  const lastLength = last?.content.length ?? 0;
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lastId, lastLength]);

  // Отправка допустима и с пустым текстом, если приложен файл (аннотацию с путём допишет сервер)
  const canSubmit = Boolean(prompt.trim()) || attachedFile !== null;

  const handleSubmit = useCallback(() => {
    const text = prompt.trim();
    if ((!text && !attachedFile) || isStreaming) return;
    setPrompt('');
    setAttachedFile(null);
    void controller.sendMessage(
      text,
      attachedFile ? { fileUuids: [attachedFile.uuid] } : undefined
    );
  }, [controller, prompt, isStreaming, attachedFile]);

  /** Выбор файла с Диска (одиночный; контракт сервера — массив, задел на мультивыбор) */
  const handleAttachFile = useCallback(() => {
    filePicker.open(
      {
        title: 'Выбрать файл с Диска',
        multiple: false,
        onSelect: (files: UploadedFile[]) => {
          if (files[0]) setAttachedFile(files[0]);
          filePicker.close();
        },
        onClose: () => filePicker.close(),
      },
      { size: 'xl' }
    );
  }, [filePicker]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleStop = useCallback(() => void controller.stopStreaming(), [controller]);

  const onCloseError = useCallback(() => controller.clearError(), [controller]);

  return (
    <Stack gap='xs' className={classes.container}>
      {/* История сообщений */}
      <Paper p={0} withBorder className={classes.messagesContainer}>
        <ScrollArea className={classes.messagesScrollArea}>
          <Box p='xs' pb={isStreaming ? 'calc(var(--mantine-spacing-xs) + 32px)' : 'xs'}>
            <Stack gap='xs'>
              {hasMoreOlder && (
                <Button
                  variant='subtle'
                  size='compact-xs'
                  loading={loadingOlder}
                  onClick={() => void controller.loadOlderMessages()}
                  className={classes.olderButton}
                >
                  Показать более ранние
                </Button>
              )}
              {messages.length === 0 && !isLoadingHistory ? (
                <Text size='xs' c='dimmed' className={classes.emptyState}>
                  Начните диалог с ассистентом. Введите сообщение в поле ввода ниже.
                </Text>
              ) : (
                <>
                  {messages.map(message => (
                    <AssistantMessageCard key={message.id} message={message} />
                  ))}
                  {error && !isStreaming && (
                    <Alert color='red' title='Ошибка' withCloseButton onClose={onCloseError}>
                      {error}
                    </Alert>
                  )}
                  <div ref={chatEndRef} className={classes.chatEndRef} />
                </>
              )}
            </Stack>
          </Box>
        </ScrollArea>

        {/* Статус текущей активности ассистента (от первого лица) */}
        {isStreaming && (
          <Group gap={4} align='center' className={classes.loadingIndicator}>
            <IconBrain size={12} />
            <Text size='xs' fw={600} c='dimmed' style={{ lineHeight: 1 }}>
              {statusLabel}
            </Text>
          </Group>
        )}
      </Paper>

      {/* Composer — единый бокс: текст сверху, тулбар со стоп/отправкой снизу */}
      <Paper
        withBorder
        radius='lg'
        className={
          pendingUserInput
            ? `${classes.composer} ${classes.composerPending}`
            : classes.composer
        }
      >
        <Stack gap={6} p='xs'>
          <ToolCallChips />

          {/* HITL: ассистент ждёт ответа пользователя */}
          {pendingUserInput && (
            <Alert
              icon={<IconInfoCircle size={12} />}
              title='Запрос ассистента'
              color='blue'
              variant='light'
              styles={{ title: { fontSize: '11px' }, message: { fontSize: '11px' } }}
            >
              <Text size='xs'>{pendingUserInput.prompt}</Text>
            </Alert>
          )}

          {/* Приложенный файл — до отправки, с возможностью снять */}
          {attachedFile && (
            <Group gap={6} wrap='nowrap' className={classes.attachedFile}>
              <IconPaperclip size={12} style={{ flexShrink: 0 }} />
              <Text size='xs' truncate>
                {attachedFile.original_name ?? attachedFile.filename}
              </Text>
              <ActionIcon
                size='xs'
                variant='subtle'
                color='gray'
                onClick={() => setAttachedFile(null)}
                aria-label='Убрать файл'
              >
                <IconX size={12} />
              </ActionIcon>
            </Group>
          )}

          <Textarea
            variant='unstyled'
            placeholder={
              isStreaming
                ? 'Ассистент отвечает…'
                : pendingUserInput
                  ? 'Введите ваш ответ на запрос ассистента…'
                  : 'Введите ваш запрос к ассистенту…'
            }
            value={prompt}
            onChange={e => setPrompt(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            minRows={1}
            maxRows={8}
            disabled={isStreaming}
            autosize
            size='sm'
            classNames={{ input: classes.composerTextarea }}
          />

          {/* Тулбар: слева хинт (в покое), справа тумблер отправка ↔ стоп */}
          <Group justify='space-between' align='center' gap={4} wrap='nowrap'>
            <Group gap={6} align='center' wrap='nowrap'>
              <ActionIcon
                variant='subtle'
                color='gray'
                radius='md'
                size='md'
                onClick={handleAttachFile}
                disabled={isStreaming}
                aria-label='Прикрепить файл с Диска'
              >
                <IconPaperclip size={16} />
              </ActionIcon>
              {!isStreaming && (
                <Text size='xs' c='dimmed' className={classes.composerHint}>
                  Ctrl+Enter — отправить
                </Text>
              )}
            </Group>
            {isStreaming ? (
              <ActionIcon
                color='red'
                variant='filled'
                radius='md'
                size='lg'
                onClick={handleStop}
                aria-label='Остановить генерацию'
              >
                <IconPlayerStopFilled size={16} />
              </ActionIcon>
            ) : (
              <ActionIcon
                variant='filled'
                radius='md'
                size='lg'
                onClick={handleSubmit}
                disabled={!canSubmit}
                aria-label={pendingUserInput ? 'Отправить ответ' : 'Отправить'}
              >
                <IconArrowUp size={16} />
              </ActionIcon>
            )}
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
